import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { weekAnchorDate } from '@/lib/cohortWeeks';
import { useAuth } from '@/contexts/AuthContext';
import { paginateQuery } from '@/lib/paginateSupabase';
import {
  calculateROI,
  calculateBCR,
  calculatePaybackMonths,
  BENEFIT_TYPE_LABELS,
  type ROICostStructure,
  type ROIBenefitItem,
  type ProjectROIData,
} from '@/data/roiModelData';

// ── Types ──

export interface DiagnosticUserLog {
  id: string;
  name: string;
  email: string;
  area: string;
  completed: boolean;
  completedAt: string | null;
  level: string | null;
  score: number | null;
  responses: any[] | null;
}

export interface DiagnosticData {
  totalParticipants: number;
  completedDiagnostics: number;
  averageScore: number;
  levelDistribution: { level: string; levelNumber: number; count: number; color: string }[];
  byArea: { area: string; score: number }[];
  userLog: DiagnosticUserLog[];
}

export interface CohortInfo {
  id: string;
  name: string;
}

export interface DeliverableModule {
  moduleId: string;
  moduleName: string;
  status: 'entregado' | 'pendiente' | 'tardío';
  score: number | null;
  deliveredAt: string | null;
  fileUrl: string | null;
  deliverableId: string | null;
  deliverableName: string | null;
  weekNumber: number | null;
}

export interface ProgressContentItem {
  id: string;
  title: string;
  completed: boolean;
  weekNumber: number | null;
}

export interface ProgressProjectItem {
  id: string;
  name: string;
  submitted: boolean;
  status: string | null;
}

export interface DeliverableParticipant {
  id: string;
  name: string;
  email: string;
  company: string;
  area: string;
  modules: DeliverableModule[];
  allContentItems: ProgressContentItem[];
  diagnosticCompleted: boolean;
  projectItems: ProgressProjectItem[];
  challengeLabSolutions: number;
  challengeLabChallenges: number;
  totalCompleted: number;
  avgScore: number;
}

export interface CohortData {
  activeParticipation: number;
  completedDeliveries: number;
  currentWeek: string;
  currentWeekNumber: number;
  cohortStartDate: string | null;
  participants: DeliverableParticipant[];
}

export interface ProjectUserLog {
  id: string;
  name: string;
  email: string;
  area: string;
  hasProject: boolean;
  projectName: string | null;
  projectStatus: string | null;
  createdAt: string | null;
  projectDescription: string | null;
  projectTools: string[] | null;
  projectImpact: string | null;
  projectScore: number | null;
}

export interface ProjectsData {
  total: number;
  withDeliverable: number;
  fromChallengeLab: number;
  avgScore: number;
  byStatus: { status: string; count: number; color: string }[];
  bySolutionType: { type: string; count: number; color: string; percentage: number }[];
  topProjects: { name: string; area: string; score: number; impact: string }[];
  userLog: ProjectUserLog[];
}

export interface AdoptionRatio {
  numerator: number;
  denominator: number;
  ratio: number;
}

export interface AdoptionData {
  overallAdoption: number;
  weeklyActiveUsers: number;
  completionRate: number;
  byDepartment: { dept: string; adoption: number }[];
  breakdown: {
    deliverables: AdoptionRatio | null;
    projects: AdoptionRatio;
    diagnostics: AdoptionRatio;
    challengeLab: AdoptionRatio;
  };
}

export interface ToolsData {
  totalTools: number;
  activeTools: number;
  topTools: { name: string; usage: number; category: string }[];
  byCategory: { category: string; count: number; usage: number }[];
}

export interface NpsUserLog {
  id: string;
  name: string;
  company: string;
  cohort: string;
  area: string;
  score: number;
  type: 'promoter' | 'passive' | 'detractor';
  comment: string | null;
  date: string;
}

export interface NpsData {
  currentNPS: number;
  totalResponses: number;
  distribution: { promoters: number; passives: number; detractors: number };
  byArea: { area: string; nps: number; responses: number }[];
  byUser: NpsUserLog[];
}

export interface ROIProjectSummary {
  projectId: string;
  projectName: string;
  company: string;
  costs: ROICostStructure;
  benefits: ROIBenefitItem[];
  totalCosts: number;
  totalBenefits: number;
  roi: number;
  bcr: number;
  paybackMonths: number;
}

export interface ROIData {
  totalInvestment: number;
  totalBenefits: number;
  roiPercentage: number;
  bcrRatio: string;
  paybackMonths: string;
  costBreakdown: { category: string; amount: number; color: string }[];
  benefitBreakdown: { category: string; amount: number; color: string }[];
  projects: ROIProjectSummary[];
}

// ── Level helpers ──
const LEVEL_NAMES = ['Iniciador', 'Explorador', 'Implementador', 'Integrador', 'Estratega', 'Transformador'];
const LEVEL_COLORS = ['#94a3b8', '#60a5fa', '#a78bfa', '#f472b6', '#f59e0b', '#22c55e'];

function getLevelName(level: number | null): string | null {
  if (level === null || level === undefined) return null;
  if (level >= 0 && level <= 5) return LEVEL_NAMES[level];
  if (level >= 1 && level <= 6) return LEVEL_NAMES[level - 1];
  return `Nivel ${level}`;
}

// ── Main hook ──

export function useEnterpriseDashboard(organizationId: string, cohortId: string, onlyWithDeliverable: boolean = false) {
  const { user, currentRole } = useAuth();
  // Espera a que currentRole resuelva (y con él, el header x-active-role que
  // las policies RLS de auditor exigen) antes de disparar cualquier query —
  // si no, un auditor puro recibe 0 filas en la primera carga y queda
  // cacheado vacío hasta un refresh manual.
  const authReady = !!user && !!currentRole;

  // ── 1. Cohorts list ──
  const cohortsQuery = useQuery({
    queryKey: ['enterprise-dashboard-cohorts', organizationId],
    queryFn: async (): Promise<CohortInfo[]> => {
      let q = supabase.from('cohorts').select('id, name').eq('is_perpetual', false).order('start_date', { ascending: false });
      if (organizationId && organizationId !== 'all') {
        q = q.eq('organization_id', organizationId);
      }
      const { data, error } = await q;
      if (error) throw error;
      return (data || []).map(c => ({ id: c.id, name: c.name }));
    },
    enabled: authReady,
  });

  // ── 2. Org profiles ──
  const profilesQuery = useQuery({
    queryKey: ['enterprise-dashboard-profiles', organizationId],
    queryFn: async () => {
      let q = supabase.from('profiles').select('id, full_name, email, area, company, organization_id, last_login');
      if (organizationId && organizationId !== 'all') {
        q = q.eq('organization_id', organizationId);
      }
      const { data, error } = await q;
      if (error) throw error;
      return data || [];
    },
    enabled: authReady,
  });

  const orgProfiles = profilesQuery.data || [];
  const orgUserIds = orgProfiles.map(p => p.id);

  // ── 2b. Cohort participant IDs (for cross-tab filtering) ──
  const cohortParticipantsQuery = useQuery({
    queryKey: ['enterprise-dashboard-cohort-participants', cohortId],
    queryFn: async () => {
      if (cohortId === 'all') return null;
      const { data, error } = await supabase
        .from('cohort_participants')
        .select('user_id')
        .eq('cohort_id', cohortId);
      if (error) throw error;
      return (data || []).map(r => r.user_id);
    },
    enabled: authReady && cohortId !== 'all',
  });

  const filteredUserIds = useMemo(() => {
    if (cohortId === 'all' || !cohortParticipantsQuery.data) return orgUserIds;
    const cohortSet = new Set(cohortParticipantsQuery.data);
    return orgUserIds.filter(id => cohortSet.has(id));
  }, [cohortId, orgUserIds, cohortParticipantsQuery.data]);

  const filteredProfiles = useMemo(() => {
    if (cohortId === 'all' || !cohortParticipantsQuery.data) return orgProfiles;
    const idSet = new Set(filteredUserIds);
    return orgProfiles.filter(p => idSet.has(p.id));
  }, [cohortId, orgProfiles, filteredUserIds, cohortParticipantsQuery.data]);

  // ── 3. Diagnostics ──
  const diagnosticsQuery = useQuery({
    queryKey: ['enterprise-dashboard-diagnostics', organizationId, cohortId],
    queryFn: async (): Promise<DiagnosticData> => {
      if (filteredUserIds.length === 0) {
        return { totalParticipants: 0, completedDiagnostics: 0, averageScore: 0, levelDistribution: [], byArea: [], userLog: [] };
      }
      const { data, error } = await supabase
        .from('participant_diagnostics')
        .select('*')
        .in('user_id', filteredUserIds);
      if (error) throw error;

      const diagMap = new Map<string, any>();
      (data || []).forEach(d => {
        const existing = diagMap.get(d.user_id);
        if (!existing || (d.completed_at && (!existing.completed_at || d.completed_at > existing.completed_at))) {
          diagMap.set(d.user_id, d);
        }
      });

      const profileMap = new Map(filteredProfiles.map(p => [p.id, p]));
      const completed = [...diagMap.values()].filter(d => d.completed_at);

      // Level distribution
      const levelCounts: Record<number, number> = {};
      completed.forEach(d => {
        const lvl = d.maturity_level ?? 0;
        levelCounts[lvl] = (levelCounts[lvl] || 0) + 1;
      });
      const levelDistribution = LEVEL_NAMES.map((name, i) => ({
        level: name,
        levelNumber: i,
        count: levelCounts[i] || 0,
        color: LEVEL_COLORS[i],
      })).filter(l => l.count > 0);

      // By area
      const areaScores: Record<string, { sum: number; count: number }> = {};
      completed.forEach(d => {
        const profile = profileMap.get(d.user_id);
        const area = profile?.area || 'Sin área';
        if (!areaScores[area]) areaScores[area] = { sum: 0, count: 0 };
        areaScores[area].sum += (d.maturity_level || 0);
        areaScores[area].count += 1;
      });
      const byArea = Object.entries(areaScores).map(([area, v]) => ({
        area,
        score: Math.round((v.sum / v.count) * 10) / 10,
      }));

      // Average score
      const avgScore = completed.length > 0
        ? Math.round((completed.reduce((s, d) => s + (d.maturity_level || 0), 0) / completed.length) * 10) / 10
        : 0;

      // User log
      const userLog: DiagnosticUserLog[] = filteredProfiles.map(p => {
        const diag = diagMap.get(p.id);
        const answers = diag?.answers;
        let responses: any[] | null = null;
        if (answers && typeof answers === 'object') {
          responses = Object.entries(answers).map(([q, a]: [string, any]) => ({
            question: q,
            answer: typeof a === 'string'
              ? a
              : a?.cannotAnswer
                ? 'No puede responder'
                : a?.text || a?.answer || '—',
          }));
        }
        return {
          id: p.id,
          name: p.full_name || p.email,
          email: p.email,
          area: p.area || 'Sin área',
          completed: !!diag?.completed_at,
          completedAt: diag?.completed_at || null,
          level: diag ? getLevelName(diag.maturity_level) : null,
          score: diag?.maturity_level || null,
          responses,
        };
      });

      return {
        totalParticipants: filteredProfiles.length,
        completedDiagnostics: completed.length,
        averageScore: avgScore,
        levelDistribution,
        byArea,
        userLog,
      };
    },
    enabled: authReady && filteredUserIds.length > 0,
  });

  // ── 4. Cohort / Deliverables / Progress ──
  const cohortDataQuery = useQuery({
    queryKey: ['enterprise-dashboard-cohort', organizationId, cohortId],
    queryFn: async (): Promise<CohortData> => {
      const empty: CohortData = { activeParticipation: 0, completedDeliveries: 0, currentWeek: '—', currentWeekNumber: 0, cohortStartDate: null, participants: [] };
      // Get cohort participants
      const cohortIds = cohortId === 'all'
        ? (cohortsQuery.data || []).map(c => c.id)
        : [cohortId];
      if (cohortIds.length === 0) return empty;

      const { data: cpData } = await supabase
        .from('cohort_participants')
        .select('user_id, cohort_id')
        .in('cohort_id', cohortIds);
      // Filter cohort participants to only those in the organization
      const allCohortUserIds = [...new Set((cpData || []).map(cp => cp.user_id))];
      const orgUserSet = new Set(orgUserIds);
      const cohortUserIds = allCohortUserIds.filter(uid => orgUserSet.has(uid));
      if (cohortUserIds.length === 0) return empty;

      // Get cohort start_date + program_id for week calculation
      let programId: string | null = null;
      let cohortStartDate: string | null = null;
      if (cohortId !== 'all') {
        const { data: cohortRow } = await (supabase as any)
          .from('cohorts')
          .select('program_id, start_date, first_session_after_start')
          .eq('id', cohortId)
          .maybeSingle();
        programId = cohortRow?.program_id || null;
        // Fecha desde la que se cuentan semanas (cohortes nuevas: inicio + 1 día).
        cohortStartDate = weekAnchorDate(cohortRow?.start_date, cohortRow?.first_session_after_start);
      }

      // Workshops + chapters in parallel
      const [allWorkshopsRes, workshopsRes, chaptersRes] = await Promise.all([
        programId
          ? supabase.from('program_workshops').select('id, week_number').eq('program_id', programId).is('deleted_at', null).order('week_number')
          : Promise.resolve({ data: [] as any[] }),
        programId
          ? supabase.from('program_workshops').select('id, title, week_number, requires_deliverable').eq('program_id', programId).eq('requires_deliverable', true).is('deleted_at', null).order('week_number')
          : Promise.resolve({ data: [] as any[] }),
        programId
          ? supabase.from('program_chapters').select('id, week_number').eq('program_id', programId)
          : Promise.resolve({ data: [] as any[] }),
      ]);
      const allWorkshops = (allWorkshopsRes.data || []) as any[];
      const workshops = (workshopsRes.data || []) as any[];
      const chapters = (chaptersRes.data || []) as any[];
      const chapterWeekById: Record<string, number | null> = {};
      chapters.forEach(c => { chapterWeekById[c.id] = c.week_number ?? null; });
      const chapterIds = chapters.map(c => c.id);

      // Mandatory content (program-wide)
      const mandatoryContent: { id: string; name: string; weekNumber: number | null }[] = [];
      if (chapterIds.length > 0) {
        const { data: cc } = await supabase
          .from('chapter_contents')
          .select('content_id, chapter_id, is_mandatory, content_bank!inner(id, name)')
          .in('chapter_id', chapterIds)
          .eq('is_mandatory', true);
        const seenWeek: Record<string, number | null> = {};
        (cc || []).forEach((row: any) => {
          const cb = row.content_bank;
          if (!cb) return;
          const w = chapterWeekById[row.chapter_id] ?? null;
          if (!(cb.id in seenWeek)) {
            seenWeek[cb.id] = w;
            mandatoryContent.push({ id: cb.id, name: cb.name, weekNumber: w });
          } else {
            const prev = seenWeek[cb.id];
            if (w !== null && (prev === null || w < prev)) {
              seenWeek[cb.id] = w;
              const idx = mandatoryContent.findIndex(m => m.id === cb.id);
              if (idx >= 0) mandatoryContent[idx].weekNumber = w;
            }
          }
        });
      }

      // Per-user data — chunkeamos cohortUserIds (URL grande) y paginamos
      // (PostgREST max-rows = 1000 trunca multi-fila por usuario silenciosamente).
      const USER_CHUNK = 200;
      const fetchChunkedPaginated = async <T,>(
        build: (ids: string[]) => (from: number, to: number) => PromiseLike<{ data: T[] | null; error: any }>,
      ): Promise<T[]> => {
        const all: T[] = [];
        for (let i = 0; i < cohortUserIds.length; i += USER_CHUNK) {
          const slice = cohortUserIds.slice(i, i + USER_CHUNK);
          if (slice.length === 0) continue;
          const rows = await paginateQuery<T>(build(slice));
          all.push(...rows);
        }
        return all;
      };

      const [deliverables, contentProgRows, diagRows, projRows, challRows, solRows] = await Promise.all([
        fetchChunkedPaginated<any>((ids) => (from, to) =>
          supabase.from('participant_deliverables').select('*').in('user_id', ids).in('cohort_id', cohortIds).range(from, to)
        ),
        fetchChunkedPaginated<{ user_id: string; content_id: string; completed: boolean }>((ids) => (from, to) =>
          supabase.from('content_progress').select('user_id, content_id, completed').in('user_id', ids).range(from, to)
        ),
        fetchChunkedPaginated<{ user_id: string; completed_at: string | null }>((ids) => (from, to) =>
          supabase.from('participant_diagnostics').select('user_id, completed_at').in('user_id', ids).range(from, to)
        ),
        fetchChunkedPaginated<{ id: string; user_id: string; name: string | null; submitted: boolean | null; status: string | null }>((ids) => (from, to) =>
          supabase.from('participant_projects').select('id, user_id, name, submitted, status').in('user_id', ids).in('cohort_id', cohortIds).range(from, to)
        ),
        fetchChunkedPaginated<{ id: string; user_id: string }>((ids) => (from, to) =>
          supabase.from('challenges').select('id, user_id').in('user_id', ids).range(from, to)
        ),
        fetchChunkedPaginated<any>((ids) => (from, to) =>
          supabase.from('challenge_solutions').select('id, challenge_id, challenges!inner(user_id)').in('challenges.user_id', ids).range(from, to)
        ),
      ]);

      // Calculate real current week based on cohort start_date — use ALL workshops as the program length
      const totalWeeks = allWorkshops.length;
      let currentWeekLabel = '—';
      let currentWeekNumber = 0;
      if (cohortStartDate && totalWeeks > 0) {
        const start = new Date(cohortStartDate);
        const now = new Date();
        const diffDays = Math.floor((now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
        const realWeek = Math.max(1, Math.ceil((diffDays + 1) / 7));
        currentWeekNumber = Math.min(realWeek, totalWeeks);
        currentWeekLabel = `Semana ${currentWeekNumber} de ${totalWeeks}`;
      } else if (totalWeeks > 0) {
        currentWeekLabel = `${totalWeeks} semanas`;
      } else if (cohortStartDate) {
        const start = new Date(cohortStartDate);
        const now = new Date();
        const diffDays = Math.floor((now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
        currentWeekNumber = Math.max(0, Math.floor(diffDays / 7) + 1);
      }

      const profileMap = new Map(orgProfiles.map(p => [p.id, p]));
      const delMap = new Map<string, any[]>();
      deliverables.forEach(d => {
        const arr = delMap.get(d.user_id) || [];
        arr.push(d);
        delMap.set(d.user_id, arr);
      });

      const completedContentByUser: Record<string, Set<string>> = {};
      contentProgRows.forEach((r) => {
        if (!r.completed) return;
        if (!completedContentByUser[r.user_id]) completedContentByUser[r.user_id] = new Set();
        completedContentByUser[r.user_id].add(r.content_id);
      });

      const diagnosticUsers = new Set<string>();
      diagRows.forEach((r) => { if (r.completed_at) diagnosticUsers.add(r.user_id); });

      const projectsByUser: Record<string, ProgressProjectItem[]> = {};
      projRows.forEach((r) => {
        if (!projectsByUser[r.user_id]) projectsByUser[r.user_id] = [];
        projectsByUser[r.user_id].push({
          id: r.id,
          name: r.name || 'Proyecto sin título',
          submitted: !!r.submitted,
          status: r.status,
        });
      });

      const challengesByUser: Record<string, number> = {};
      challRows.forEach((r) => { challengesByUser[r.user_id] = (challengesByUser[r.user_id] || 0) + 1; });
      const solutionsByUser: Record<string, number> = {};
      solRows.forEach((r: any) => {
        const uid = (r.challenges as any)?.user_id;
        if (uid) solutionsByUser[uid] = (solutionsByUser[uid] || 0) + 1;
      });

      const totalDelivered = deliverables.filter(d => d.status === 'validated' || d.status === 'submitted').length;
      const participants: DeliverableParticipant[] = cohortUserIds.map(uid => {
        const profile = profileMap.get(uid);
        const userDels = delMap.get(uid) || [];
        const modules: DeliverableModule[] = workshops.map(w => {
          const del = userDels.find(d => d.workshop_id === w.id);
          let status: 'entregado' | 'pendiente' | 'tardío' = 'pendiente';
          if (del) {
            if (del.status === 'validated' || del.status === 'submitted') status = 'entregado';
            else if (del.status === 'needs_revision') status = 'tardío';
            else status = 'pendiente';
          }
          return {
            moduleId: w.id,
            moduleName: w.title || `Módulo ${w.week_number}`,
            status,
            score: del?.score || null,
            deliveredAt: del?.submitted_at || null,
            fileUrl: del?.file_url || null,
            deliverableId: del?.id || null,
            deliverableName: del?.name || null,
            weekNumber: w.week_number ?? null,
          };
        });
        const completedCount = modules.filter(m => m.status === 'entregado').length;
        const scored = modules.filter(m => m.score !== null);
        const avgScore = scored.length > 0 ? Math.round(scored.reduce((s, m) => s + (m.score || 0), 0) / scored.length) : 0;

        const completedSet = completedContentByUser[uid] || new Set();
        const allContentItems: ProgressContentItem[] = mandatoryContent.map(mc => ({
          id: mc.id,
          title: mc.name,
          completed: completedSet.has(mc.id),
          weekNumber: mc.weekNumber,
        }));

        return {
          id: uid,
          name: profile?.full_name || 'Sin nombre',
          email: profile?.email || '',
          company: profile?.company || '',
          area: profile?.area || 'Sin área',
          modules,
          allContentItems,
          diagnosticCompleted: diagnosticUsers.has(uid),
          projectItems: projectsByUser[uid] || [],
          challengeLabSolutions: solutionsByUser[uid] || 0,
          challengeLabChallenges: challengesByUser[uid] || 0,
          totalCompleted: completedCount,
          avgScore,
        };
      });

      const activeCount = participants.filter(p => p.totalCompleted > 0).length;
      const activeParticipation = cohortUserIds.length > 0 ? Math.round((activeCount / cohortUserIds.length) * 100) : 0;

      return {
        activeParticipation,
        completedDeliveries: totalDelivered,
        currentWeek: currentWeekLabel,
        currentWeekNumber,
        cohortStartDate,
        participants,
      };
    },
    enabled: authReady && (cohortsQuery.data || []).length > 0,
  });

  // ── 5. Projects ──
  const projectsQuery = useQuery({
    queryKey: ['enterprise-dashboard-projects', organizationId, cohortId, onlyWithDeliverable],
    queryFn: async (): Promise<ProjectsData> => {
      if (filteredUserIds.length === 0) return { total: 0, withDeliverable: 0, fromChallengeLab: 0, avgScore: 0, byStatus: [], bySolutionType: [], topProjects: [], userLog: [] };

      // Chunkear filteredUserIds + paginar (PostgREST max-rows = 1000)
      const USER_CHUNK = 200;
      const projectsRaw: any[] = [];
      for (let i = 0; i < filteredUserIds.length; i += USER_CHUNK) {
        const slice = filteredUserIds.slice(i, i + USER_CHUNK);
        if (slice.length === 0) continue;
        const page = await paginateQuery<any>((from, to) => {
          let q = supabase
            .from('participant_projects')
            .select('*')
            .in('user_id', slice)
            .range(from, to);
          if (cohortId !== 'all') q = q.eq('cohort_id', cohortId);
          return q;
        });
        projectsRaw.push(...page);
      }

      const hasFile = (p: any) => {
        const single = typeof p.file_url === 'string' && p.file_url.trim() !== '';
        const arr = Array.isArray(p.file_urls) ? p.file_urls.length > 0 : false;
        return single || arr;
      };
      const projects = onlyWithDeliverable
        ? (projectsRaw || []).filter(hasFile)
        : (projectsRaw || []);

      const projectIds = (projects || []).map(p => p.id);
      const [evalData, toolsRows] = await Promise.all([
        projectIds.length > 0
          ? paginateQuery<any>((from, to) =>
              supabase.from('project_evaluations').select('*').in('project_id', projectIds).range(from, to)
            )
          : Promise.resolve([] as any[]),
        projectIds.length > 0
          ? paginateQuery<{ project_id: string; tool_name: string }>((from, to) =>
              supabase.from('project_tools').select('project_id, tool_name').in('project_id', projectIds).range(from, to)
            )
          : Promise.resolve([] as { project_id: string; tool_name: string }[]),
      ]);

      const evalMap = new Map(evalData.map((e: any) => [e.project_id, e]));
      const toolsMap = new Map<string, string[]>();
      toolsRows.forEach((t) => {
        const arr = toolsMap.get(t.project_id) || [];
        arr.push(t.tool_name);
        toolsMap.set(t.project_id, arr);
      });
      const profileMap = new Map(filteredProfiles.map(p => [p.id, p]));

      // Status mapping
      const statusLabels: Record<string, string> = {
        ideation: 'En ideación',
        development: 'En progreso',
        testing: 'En revisión',
        completed: 'Completados',
        submitted: 'Completados',
      };
      const statusColors: Record<string, string> = {
        'Completados': '#22c55e',
        'En progreso': '#3b82f6',
        'En revisión': '#f59e0b',
        'En ideación': '#94a3b8',
      };

      const statusCounts: Record<string, number> = {};
      (projects || []).forEach(p => {
        const label = statusLabels[p.status || 'ideation'] || p.status || 'Otro';
        statusCounts[label] = (statusCounts[label] || 0) + 1;
      });
      const byStatus = Object.entries(statusCounts).map(([status, count]) => ({
        status,
        count,
        color: statusColors[status] || '#94a3b8',
      }));

      // Solution type mapping (mismo orden que useProjects SOLUTION_TYPE_MAP)
      const solutionTypeLabels: Record<number, string> = {
        1: 'Creación de Contenido',
        2: 'Vibecoding',
        3: 'Agentes IA',
        4: 'Automatización con IA',
        5: 'Análisis de Datos',
      };
      const solutionTypeColors: Record<string, string> = {
        'Creación de Contenido': '#3b82f6',
        'Vibecoding': '#a855f7',
        'Agentes IA': '#22c55e',
        'Automatización con IA': '#f59e0b',
        'Análisis de Datos': '#06b6d4',
      };
      // Solo proyectos con tipo de solución asignado
      const projectsWithType = (projects || []).filter(p => p.solution_type != null && solutionTypeLabels[p.solution_type]);
      const solutionCounts: Record<string, number> = {};
      projectsWithType.forEach(p => {
        const label = solutionTypeLabels[p.solution_type!];
        solutionCounts[label] = (solutionCounts[label] || 0) + 1;
      });
      const totalWithType = projectsWithType.length;
      const bySolutionType = Object.entries(solutionCounts).map(([type, count]) => ({
        type,
        count,
        color: solutionTypeColors[type] || '#94a3b8',
        percentage: totalWithType > 0 ? Math.round((count / totalWithType) * 100) : 0,
      })).sort((a, b) => b.count - a.count);

      // Top projects by score
      const scored = (projects || [])
        .map(p => ({ ...p, ev: evalMap.get(p.id) }))
        .filter(p => p.ev?.total_score)
        .sort((a, b) => (b.ev!.total_score || 0) - (a.ev!.total_score || 0))
        .slice(0, 4);
      const topProjects = scored.map(p => ({
        name: p.name,
        area: profileMap.get(p.user_id)?.area || 'Sin área',
        score: p.ev!.total_score || 0,
        impact: (p.ev!.total_score || 0) >= 80 ? 'Alto' : (p.ev!.total_score || 0) >= 60 ? 'Medio' : 'Bajo',
      }));

      // User log — all projects (one row per project, plus users without projects)
      const projectsByUser = new Map<string, any[]>();
      (projects || []).forEach(p => {
        const arr = projectsByUser.get(p.user_id) || [];
        arr.push(p);
        projectsByUser.set(p.user_id, arr);
      });

      const userLog: ProjectUserLog[] = [];
      filteredProfiles.forEach(p => {
        const userProjects = projectsByUser.get(p.id);
        if (userProjects && userProjects.length > 0) {
          userProjects.forEach(proj => {
            const ev = evalMap.get(proj.id);
            userLog.push({
              id: p.id,
              name: p.full_name || p.email,
              email: p.email,
              area: p.area || 'Sin área',
              hasProject: true,
              projectName: proj.name,
              projectStatus: statusLabels[proj.status || 'ideation'] || proj.status,
              createdAt: proj.created_at || null,
              projectDescription: proj.solution_description || proj.problem || null,
              projectTools: toolsMap.get(proj.id) || null,
              projectImpact: ev ? ((ev.total_score || 0) >= 80 ? 'Alto' : (ev.total_score || 0) >= 60 ? 'Medio' : 'Bajo') : null,
              projectScore: ev?.total_score || null,
            });
          });
        } else {
          userLog.push({
            id: p.id,
            name: p.full_name || p.email,
            email: p.email,
            area: p.area || 'Sin área',
            hasProject: false,
            projectName: null,
            projectStatus: null,
            createdAt: null,
            projectDescription: null,
            projectTools: null,
            projectImpact: null,
            projectScore: null,
          });
        }
      });

      const withDeliverable = (projects || []).filter(hasFile).length;
      const fromChallengeLab = (projects || []).filter((p: any) => p.source_session_id).length;
      const scoredAll = (projects || [])
        .map((p: any) => evalMap.get(p.id)?.total_score)
        .filter((s: any): s is number => typeof s === 'number' && s > 0);
      const avgScore = scoredAll.length > 0
        ? Math.round(scoredAll.reduce((a, b) => a + b, 0) / scoredAll.length)
        : 0;

      return {
        total: (projects || []).length,
        withDeliverable,
        fromChallengeLab,
        avgScore,
        byStatus,
        bySolutionType,
        topProjects,
        userLog,
      };
    },
    enabled: authReady && filteredUserIds.length > 0,
  });

  // ── 6. Adoption (índice compuesto) ──
  const adoptionQuery = useQuery({
    queryKey: ['enterprise-dashboard-adoption-v2', organizationId, cohortId],
    queryFn: async (): Promise<AdoptionData> => {
      const emptyRatio: AdoptionRatio = { numerator: 0, denominator: 0, ratio: 0 };
      const emptyData: AdoptionData = {
        overallAdoption: 0,
        weeklyActiveUsers: 0,
        completionRate: 0,
        byDepartment: [],
        breakdown: { deliverables: null, projects: emptyRatio, diagnostics: emptyRatio, challengeLab: emptyRatio },
      };
      if (filteredUserIds.length === 0) return emptyData;

      // Carga base — chunkeamos filteredUserIds y paginamos por seguridad
      const USER_CHUNK = 200;
      const fetchByUsers = async <T,>(
        build: (ids: string[]) => (from: number, to: number) => PromiseLike<{ data: T[] | null; error: any }>,
      ): Promise<T[]> => {
        const all: T[] = [];
        for (let i = 0; i < filteredUserIds.length; i += USER_CHUNK) {
          const slice = filteredUserIds.slice(i, i + USER_CHUNK);
          if (slice.length === 0) continue;
          const rows = await paginateQuery<T>(build(slice));
          all.push(...rows);
        }
        return all;
      };

      const [diagData, projData, delData, streaksData, cohortPartData, challengesData] = await Promise.all([
        fetchByUsers<{ user_id: string }>((ids) => (from, to) =>
          supabase.from('participant_diagnostics').select('user_id').in('user_id', ids).range(from, to)
        ),
        fetchByUsers<{ user_id: string; submitted: boolean | null; status: string | null }>((ids) => (from, to) =>
          supabase.from('participant_projects').select('user_id, submitted, status').in('user_id', ids).range(from, to)
        ),
        fetchByUsers<{ user_id: string; cohort_id: string | null; status: string | null }>((ids) => (from, to) =>
          supabase.from('participant_deliverables').select('user_id, cohort_id, status').in('user_id', ids).range(from, to)
        ),
        fetchByUsers<{ user_id: string; last_activity_date: string | null }>((ids) => (from, to) =>
          supabase.from('participant_streaks').select('user_id, last_activity_date').in('user_id', ids).range(from, to)
        ),
        fetchByUsers<{ user_id: string; cohort_id: string }>((ids) => (from, to) =>
          supabase.from('cohort_participants').select('user_id, cohort_id').in('user_id', ids).range(from, to)
        ),
        fetchByUsers<{ user_id: string }>((ids) => (from, to) =>
          supabase.from('challenges').select('user_id').in('user_id', ids).range(from, to)
        ),
      ]);

      const totalParticipants = filteredProfiles.length;

      // ── Ratio 1: Entregables (solo participantes con cohorte) ──
      const cohortParts = cohortPartData.filter(cp => cohortId === 'all' || cp.cohort_id === cohortId);
      // # de participantes por cohorte (dentro del filtro)
      const participantsPerCohort: Record<string, number> = {};
      const cohortUsersSet = new Set<string>();
      cohortParts.forEach(cp => {
        participantsPerCohort[cp.cohort_id] = (participantsPerCohort[cp.cohort_id] || 0) + 1;
        cohortUsersSet.add(cp.user_id);
      });

      let deliverablesRatio: AdoptionRatio | null = null;
      const involvedCohortIds = Object.keys(participantsPerCohort);
      if (involvedCohortIds.length > 0) {
        // Programa de cada cohorte
        const { data: cohortsData } = await supabase
          .from('cohorts').select('id, program_id').in('id', involvedCohortIds);
        const cohortProgram = new Map<string, string>();
        (cohortsData || []).forEach(c => { if (c.program_id) cohortProgram.set(c.id, c.program_id); });

        // Workshops con entregable por programa
        const programIds = Array.from(new Set(Array.from(cohortProgram.values())));
        const workshopsPerProgram: Record<string, number> = {};
        if (programIds.length > 0) {
          const { data: wks } = await supabase
            .from('program_workshops').select('program_id')
            .in('program_id', programIds)
            .eq('requires_deliverable', true)
            .is('deleted_at', null);
          (wks || []).forEach(w => {
            workshopsPerProgram[w.program_id] = (workshopsPerProgram[w.program_id] || 0) + 1;
          });
        }

        // Denominador = Σ (workshops_programa × participantes_cohorte)
        let denom = 0;
        involvedCohortIds.forEach(cid => {
          const progId = cohortProgram.get(cid);
          const wksCount = progId ? (workshopsPerProgram[progId] || 0) : 0;
          denom += wksCount * (participantsPerCohort[cid] || 0);
        });

        // Numerador = entregables enviados (submitted/validated) por usuarios de cohorte en sus cohortes
        const cohortIdSet = new Set(involvedCohortIds);
        const numer = delData.filter(d =>
          (d.status === 'submitted' || d.status === 'validated')
          && cohortUsersSet.has(d.user_id)
          && d.cohort_id && cohortIdSet.has(d.cohort_id)
        ).length;

        deliverablesRatio = {
          numerator: numer,
          denominator: denom,
          ratio: denom > 0 ? Math.min(100, Math.round((numer / denom) * 100)) : 0,
        };
      }

      // ── Ratio 2: Proyectos entregados / # participantes ──
      const submittedProjectUsers = new Set(
        projData
          .filter(p => p.submitted === true || p.status === 'completed')
          .map(p => p.user_id),
      );
      const projectsRatio: AdoptionRatio = {
        numerator: submittedProjectUsers.size,
        denominator: totalParticipants,
        ratio: totalParticipants > 0 ? Math.round((submittedProjectUsers.size / totalParticipants) * 100) : 0,
      };

      // ── Ratio 3: Diagnósticos / # participantes ──
      const diagUsers = new Set(diagData.map(d => d.user_id));
      const diagnosticsRatio: AdoptionRatio = {
        numerator: diagUsers.size,
        denominator: totalParticipants,
        ratio: totalParticipants > 0 ? Math.round((diagUsers.size / totalParticipants) * 100) : 0,
      };

      // ── Ratio 4: Laboratorios de retos / # participantes ──
      const challengeUsers = new Set(challengesData.map(c => c.user_id));
      const challengeLabRatio: AdoptionRatio = {
        numerator: challengeUsers.size,
        denominator: totalParticipants,
        ratio: totalParticipants > 0 ? Math.round((challengeUsers.size / totalParticipants) * 100) : 0,
      };

      // ── Adopción general = promedio de ratios con denominador > 0 ──
      const activeRatios = [
        deliverablesRatio && deliverablesRatio.denominator > 0 ? deliverablesRatio.ratio : null,
        projectsRatio.denominator > 0 ? projectsRatio.ratio : null,
        diagnosticsRatio.denominator > 0 ? diagnosticsRatio.ratio : null,
        challengeLabRatio.denominator > 0 ? challengeLabRatio.ratio : null,
      ].filter((v): v is number => v !== null);
      const overallAdoption = activeRatios.length > 0
        ? Math.round(activeRatios.reduce((a, b) => a + b, 0) / activeRatios.length)
        : 0;

      // ── Métricas secundarias ──
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const sevenDaysAgoStr = sevenDaysAgo.toISOString().split('T')[0];
      // participant_streaks guarda una fila por (user_id, scope). Deduplicar por usuario.
      const weeklyActiveUsers = new Set(
        streaksData
          .filter(s => s.last_activity_date && s.last_activity_date >= sevenDaysAgoStr)
          .map(s => s.user_id)
      ).size;

      const totalDels = delData.length;
      const completedDels = delData.filter(d => d.status === 'validated' || d.status === 'submitted').length;
      const completionRate = totalDels > 0 ? Math.round((completedDels / totalDels) * 100) : 0;

      // ── Adopción por departamento (mismo índice compuesto por área) ──
      type DeptAgg = {
        total: number;
        cohortTotal: number;
        delivNum: number;
        delivDenWorkshops: number; // workshops esperados por usuario de cohorte, sumado
        projUsers: Set<string>;
        diagUsers: Set<string>;
        challUsers: Set<string>;
      };
      const deptMap: Record<string, DeptAgg> = {};
      // mapas auxiliares
      const userCohortMap = new Map<string, string>(); // user -> cohort_id
      cohortParts.forEach(cp => { userCohortMap.set(cp.user_id, cp.cohort_id); });
      const userToWorkshops = (uid: string): number => {
        const cid = userCohortMap.get(uid);
        if (!cid) return 0;
        // recuperar program_id desde participantsPerCohort no es suficiente; reusar maps externos
        return 0; // se calculará abajo si hace falta
      };

      filteredProfiles.forEach(p => {
        const dept = p.area || 'Sin área';
        if (!deptMap[dept]) {
          deptMap[dept] = { total: 0, cohortTotal: 0, delivNum: 0, delivDenWorkshops: 0, projUsers: new Set(), diagUsers: new Set(), challUsers: new Set() };
        }
        deptMap[dept].total += 1;
        if (cohortUsersSet.has(p.id)) deptMap[dept].cohortTotal += 1;
      });
      // Para evitar otra ida al servidor, recomponemos workshops esperados desde lo ya cargado en deliverablesRatio:
      const workshopsPerCohortForDept: Record<string, number> = {};
      if (deliverablesRatio) {
        // reusar ya está en variables del bloque anterior; reextraer para scope:
        // (se calculó arriba como wksCount per cohort; lo recalculamos resumidamente)
      }
      // Conteos per-user para proyectos/diag/challenges
      const profileDept = new Map<string, string>();
      filteredProfiles.forEach(p => profileDept.set(p.id, p.area || 'Sin área'));
      submittedProjectUsers.forEach(uid => { const d = profileDept.get(uid); if (d) deptMap[d].projUsers.add(uid); });
      diagUsers.forEach(uid => { const d = profileDept.get(uid); if (d) deptMap[d].diagUsers.add(uid); });
      challengeUsers.forEach(uid => { const d = profileDept.get(uid); if (d) deptMap[d].challUsers.add(uid); });

      const byDepartment = Object.entries(deptMap)
        .map(([dept, v]) => {
          const ratios: number[] = [];
          if (v.total > 0) {
            ratios.push(Math.round((v.projUsers.size / v.total) * 100));
            ratios.push(Math.round((v.diagUsers.size / v.total) * 100));
            ratios.push(Math.round((v.challUsers.size / v.total) * 100));
          }
          const adoption = ratios.length > 0 ? Math.round(ratios.reduce((a, b) => a + b, 0) / ratios.length) : 0;
          return { dept, adoption };
        })
        .sort((a, b) => b.adoption - a.adoption);

      return {
        overallAdoption,
        weeklyActiveUsers,
        completionRate,
        byDepartment,
        breakdown: {
          deliverables: deliverablesRatio,
          projects: projectsRatio,
          diagnostics: diagnosticsRatio,
          challengeLab: challengeLabRatio,
        },
      };
    },
    enabled: authReady && filteredUserIds.length > 0,
  });

  // ── 7. Tools ──
  const toolsQuery = useQuery({
    queryKey: ['enterprise-dashboard-tools', organizationId, cohortId],
    queryFn: async (): Promise<ToolsData> => {
      if (filteredUserIds.length === 0) return { totalTools: 0, activeTools: 0, topTools: [], byCategory: [] };

      // Get project tools for filtered users
      let projQuery = supabase
        .from('participant_projects')
        .select('id')
        .in('user_id', filteredUserIds);
      if (cohortId !== 'all') {
        projQuery = projQuery.eq('cohort_id', cohortId);
      }
      const { data: projects } = await projQuery;
      const projectIds = (projects || []).map(p => p.id);

      let toolFreq: Record<string, number> = {};
      if (projectIds.length > 0) {
        const { data: tools } = await supabase
          .from('project_tools')
          .select('tool_name')
          .in('project_id', projectIds);
        (tools || []).forEach(t => {
          toolFreq[t.tool_name] = (toolFreq[t.tool_name] || 0) + 1;
        });
      }

      // Also get licenses
      let licenseQuery = supabase.from('ai_tool_licenses').select('*');
      if (organizationId && organizationId !== 'all') {
        licenseQuery = licenseQuery.eq('organization_id', organizationId);
      }
      const { data: licenses } = await licenseQuery;
      (licenses || []).forEach(l => {
        if (l.tool_name && !toolFreq[l.tool_name]) {
          toolFreq[l.tool_name] = 0;
        }
      });

      const totalTools = Object.keys(toolFreq).length;
      const activeTools = Object.values(toolFreq).filter(v => v > 0).length;
      const maxUsage = Math.max(...Object.values(toolFreq), 1);

      const topTools = Object.entries(toolFreq)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 6)
        .map(([name, count]) => ({
          name,
          usage: Math.round((count / maxUsage) * 100),
          category: (licenses || []).find(l => l.tool_name === name)?.category || 'General',
        }));

      // By category
      const catMap: Record<string, { count: number; totalUsage: number }> = {};
      Object.entries(toolFreq).forEach(([name, count]) => {
        const cat = (licenses || []).find(l => l.tool_name === name)?.category || 'General';
        if (!catMap[cat]) catMap[cat] = { count: 0, totalUsage: 0 };
        catMap[cat].count += 1;
        catMap[cat].totalUsage += count;
      });
      const byCategory = Object.entries(catMap).map(([category, v]) => ({
        category,
        count: v.count,
        usage: v.count > 0 ? Math.round((v.totalUsage / (v.count * maxUsage)) * 100) : 0,
      }));

      return { totalTools, activeTools, topTools, byCategory };
    },
    enabled: authReady && filteredUserIds.length > 0,
  });

  // ── 8. NPS ──
  const npsQuery = useQuery({
    queryKey: ['enterprise-dashboard-nps', organizationId, cohortId],
    queryFn: async (): Promise<NpsData> => {
      // Se consulta nps_responses DIRECTAMENTE (join a profiles), no vía filteredUserIds:
      // ese arreglo se trunca con el cap de 1000 de PostgREST (profilesQuery sin paginar)
      // y hacía que el super_admin viera 0 aunque hubiera respuestas. RLS: super_admin ve
      // todas ("SA read all nps"), admin ve las de su org. El filtro por org/cohorte es en JS.
      const rows = await paginateQuery<any>((from, to) =>
        supabase
          .from('nps_responses')
          .select('id, score, comment, cohort_id, created_at, user_id, profiles(full_name, company, area, organization_id)')
          .order('created_at', { ascending: false })
          .range(from, to),
      );
      const cohortMap = new Map((cohortsQuery.data || []).map(c => [c.id, c.name]));

      let responses = (rows || []).filter((r: any) => r.profiles);
      if (organizationId && organizationId !== 'all') {
        responses = responses.filter((r: any) => r.profiles?.organization_id === organizationId);
      }
      if (cohortId !== 'all') {
        responses = responses.filter((r: any) => r.cohort_id === cohortId);
      }

      const promoters = responses.filter((r: any) => (r.score || 0) >= 9).length;
      const passives = responses.filter((r: any) => (r.score || 0) >= 7 && (r.score || 0) <= 8).length;
      const detractors = responses.filter((r: any) => (r.score || 0) <= 6).length;
      const total = responses.length;
      const currentNPS = total > 0 ? Math.round(((promoters - detractors) / total) * 100) : 0;

      // By area
      const areaMap: Record<string, { scores: number[]; count: number }> = {};
      responses.forEach((r: any) => {
        const area = r.profiles?.area || 'Sin área';
        if (!areaMap[area]) areaMap[area] = { scores: [], count: 0 };
        areaMap[area].scores.push(r.score || 0);
        areaMap[area].count += 1;
      });
      const byArea = Object.entries(areaMap).map(([area, v]) => {
        const p = v.scores.filter(s => s >= 9).length;
        const d = v.scores.filter(s => s <= 6).length;
        const nps = v.count > 0 ? Math.round(((p - d) / v.count) * 100) : 0;
        return { area, nps, responses: v.count };
      }).sort((a, b) => b.nps - a.nps);

      // User log
      const byUser: NpsUserLog[] = responses.map((r: any) => {
        const score = r.score || 0;
        return {
          id: r.id,
          name: r.profiles?.full_name || 'Sin nombre',
          company: r.profiles?.company || '',
          cohort: r.cohort_id ? (cohortMap.get(r.cohort_id) || 'Sin cohorte') : 'Sin cohorte',
          area: r.profiles?.area || 'Sin área',
          score,
          type: score >= 9 ? 'promoter' : score >= 7 ? 'passive' : 'detractor',
          comment: r.comment || null,
          date: r.created_at,
        };
      });

      return { currentNPS, totalResponses: total, distribution: { promoters, passives, detractors }, byArea, byUser };
    },
    enabled: authReady,
  });

  // ── 9. ROI ──
  const roiQuery = useQuery({
    queryKey: ['enterprise-dashboard-roi', organizationId, cohortId],
    queryFn: async (): Promise<ROIData> => {
      if (filteredUserIds.length === 0) {
        return { totalInvestment: 0, totalBenefits: 0, roiPercentage: 0, bcrRatio: '0', paybackMonths: '0', costBreakdown: [], benefitBreakdown: [], projects: [] };
      }

      let roiQ = supabase
        .from('participant_projects')
        .select('id, name, user_id, roi_data')
        .in('user_id', filteredUserIds)
        .not('roi_data', 'is', null);
      if (cohortId !== 'all') {
        roiQ = roiQ.eq('cohort_id', cohortId);
      }
      const { data: projects } = await roiQ;

      const profileMap = new Map(filteredProfiles.map(p => [p.id, p]));

      const roiProjects: ROIProjectSummary[] = (projects || [])
        .map(p => {
          const roi = p.roi_data as unknown as ProjectROIData;
          if (!roi?.costs || !roi?.benefits) return null;
          const totalCosts = roi.costs.subscriptions + roi.costs.tokenConsumption + roi.costs.personnel + roi.costs.training;
          const totalBenefits = roi.benefits.reduce((s, b) => s + (b.type === 'not_defined' ? 0 : b.amount), 0);
          const profile = profileMap.get(p.user_id);
          return {
            projectId: p.id,
            projectName: p.name,
            company: profile?.company || 'Sin empresa',
            costs: roi.costs,
            benefits: roi.benefits,
            totalCosts,
            totalBenefits,
            roi: calculateROI(totalBenefits, totalCosts),
            bcr: calculateBCR(totalBenefits, totalCosts),
            paybackMonths: calculatePaybackMonths(totalBenefits, totalCosts),
          };
        })
        .filter(Boolean) as ROIProjectSummary[];

      const totalInvestment = roiProjects.reduce((s, p) => s + p.totalCosts, 0);
      const totalBenefits = roiProjects.reduce((s, p) => s + p.totalBenefits, 0);
      const roiPercentage = Math.round(calculateROI(totalBenefits, totalInvestment));
      const bcrRatio = calculateBCR(totalBenefits, totalInvestment).toFixed(2);
      const paybackMonths = calculatePaybackMonths(totalBenefits, totalInvestment).toFixed(1);

      // Cost breakdown
      const costBreakdown = [
        { category: 'Suscripciones y Licencias', amount: roiProjects.reduce((s, p) => s + p.costs.subscriptions, 0), color: '#3b82f6' },
        { category: 'Consumo de Tokens', amount: roiProjects.reduce((s, p) => s + p.costs.tokenConsumption, 0), color: '#8b5cf6' },
        { category: 'Capital Humano', amount: roiProjects.reduce((s, p) => s + p.costs.personnel, 0), color: '#f59e0b' },
        { category: 'Capacitación', amount: roiProjects.reduce((s, p) => s + p.costs.training, 0), color: '#10b981' },
      ];

      // Benefit breakdown
      const benefitMap: Record<string, number> = {};
      roiProjects.forEach(p => {
        p.benefits.forEach(b => {
          if (b.type !== 'not_defined') {
            const label = BENEFIT_TYPE_LABELS[b.type] || b.type;
            benefitMap[label] = (benefitMap[label] || 0) + b.amount;
          }
        });
      });
      const colors = ['#22c55e', '#3b82f6', '#f59e0b', '#8b5cf6', '#06b6d4', '#f97316'];
      const benefitBreakdown = Object.entries(benefitMap).map(([category, amount], i) => ({
        category,
        amount,
        color: colors[i % colors.length],
      }));

      return { totalInvestment, totalBenefits, roiPercentage, bcrRatio, paybackMonths, costBreakdown, benefitBreakdown, projects: roiProjects };
    },
    enabled: authReady && filteredUserIds.length > 0,
  });

  const isLoading = profilesQuery.isLoading || diagnosticsQuery.isLoading || projectsQuery.isLoading || adoptionQuery.isLoading || toolsQuery.isLoading || npsQuery.isLoading || roiQuery.isLoading;

  return {
    cohorts: cohortsQuery.data || [],
    diagnostics: diagnosticsQuery.data || { totalParticipants: 0, completedDiagnostics: 0, averageScore: 0, levelDistribution: [], byArea: [], userLog: [] },
    cohortData: cohortDataQuery.data || { activeParticipation: 0, completedDeliveries: 0, currentWeek: '—', currentWeekNumber: 0, cohortStartDate: null, participants: [] },
    projects: projectsQuery.data || { total: 0, withDeliverable: 0, fromChallengeLab: 0, avgScore: 0, byStatus: [], bySolutionType: [], topProjects: [], userLog: [] },
    adoption: adoptionQuery.data || { overallAdoption: 0, weeklyActiveUsers: 0, completionRate: 0, byDepartment: [], breakdown: { deliverables: null, projects: { numerator: 0, denominator: 0, ratio: 0 }, diagnostics: { numerator: 0, denominator: 0, ratio: 0 }, challengeLab: { numerator: 0, denominator: 0, ratio: 0 } } },
    tools: toolsQuery.data || { totalTools: 0, activeTools: 0, topTools: [], byCategory: [] },
    nps: npsQuery.data || { currentNPS: 0, totalResponses: 0, distribution: { promoters: 0, passives: 0, detractors: 0 }, byArea: [], byUser: [] },
    roi: roiQuery.data || { totalInvestment: 0, totalBenefits: 0, roiPercentage: 0, bcrRatio: '0', paybackMonths: '0', costBreakdown: [], benefitBreakdown: [], projects: [] },
    isLoading,
    isProfilesLoading: profilesQuery.isLoading,
  };
}
